import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CopilotApiService } from '../../services/copilot-api-service';
import { ToastService } from '../../services/toast.service';
import { DaysRangeDialog } from './dialog/days-range-dialog';
import { isMetricsTimezone, METRICS_TIMEZONES, MetricsTimezone } from '../../model/metrics-timezone';
import { TimezoneSelector } from '../../common/components/timezone-selector/timezone-selector';

@Component({
  selector: 'app-metrics',
  standalone: true,
  imports: [CommonModule, TranslateModule, DaysRangeDialog, TimezoneSelector],
  templateUrl: './metrics.html',
  styleUrl: './metrics.css'
})
export class Metrics implements OnInit {
  private static readonly TIMEZONE_STORAGE_KEY = 'metrics:selectedTimezone';
  private readonly api = inject(CopilotApiService);
  private readonly toast = inject(ToastService);
  private readonly i18n = inject(TranslateService);

  showDaysDialog = signal(false);
  downloadingRecent = signal(false);
  downloadingAccepted = signal(false);
  showOppsDialog = signal(false);
  downloadingOpps = signal(false);
  readonly timezoneOptions = METRICS_TIMEZONES;
  selectedTimezone = signal<MetricsTimezone>('UTC');

  ngOnInit(): void {
    try {
      const storedTimezone = localStorage.getItem(Metrics.TIMEZONE_STORAGE_KEY);
      if (storedTimezone && isMetricsTimezone(storedTimezone)) {
        this.selectedTimezone.set(storedTimezone);
      }
    } catch {}
  }

  onTimezoneChange(value: string): void {
    if (isMetricsTimezone(value)) {
      this.selectedTimezone.set(value);
      try {
        localStorage.setItem(Metrics.TIMEZONE_STORAGE_KEY, value);
      } catch {}
    }
  }

  openRecentDialog(): void {
    this.showDaysDialog.set(true);
  }

  closeRecentDialog(): void {
    this.showDaysDialog.set(false);
  }

  openOppsDialog(): void {
    this.showOppsDialog.set(true);
  }

  closeOppsDialog(): void {
    this.showOppsDialog.set(false);
  }

  private extractFilenameFromDisposition(disposition: string | null): string | null {
    if (!disposition) return null;
    // content-disposition: attachment; filename="name.ext"
    const match = /filename\*?=(?:UTF-8''|")?([^\";]+)/i.exec(disposition);
    if (match && match[1]) {
      try {
        return decodeURIComponent(match[1].replace(/\"/g, '').trim());
      } catch {
        return match[1].replace(/\"/g, '').trim();
      }
    }
    return null;
  }

  private triggerDownload(blob: Blob, filenameFallback: string, disposition: string | null): void {
    const filename = this.extractFilenameFromDisposition(disposition) || filenameFallback;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  onConfirmDays(days: number): void {
    this.downloadingRecent.set(true);
    this.api.exportRecentlyRegisteredDrivers(days, this.selectedTimezone()).subscribe({
      next: (resp) => {
        const disposition = resp.headers.get('Content-Disposition') || resp.headers.get('content-disposition');
        this.triggerDownload(resp.body as Blob, 'driver-login-metrics.xlsx', disposition);
        this.downloadingRecent.set(false);
      },
      error: () => {
        this.downloadingRecent.set(false);
        this.toast.error(this.i18n.instant('metrics.error_download'));
      }
    });
    this.closeRecentDialog();
  }

  downloadAcceptedOptimizations(): void {
    this.downloadingAccepted.set(true);
    this.api.exportAcceptedOptimizations(this.selectedTimezone()).subscribe({
      next: (resp) => {
        const disposition = resp.headers.get('Content-Disposition') || resp.headers.get('content-disposition');
        this.triggerDownload(resp.body as Blob, 'accepted-optimizations.xlsx', disposition);
        this.downloadingAccepted.set(false);
      },
      error: () => {
        this.downloadingAccepted.set(false);
        this.toast.error(this.i18n.instant('metrics.error_download'));
      }
    });
  }

  onConfirmOpps(e: { days: number; includeNotifications?: boolean; includeViolations?: boolean }): void {
    this.downloadingOpps.set(true);
    this.api.exportOptimizationOpportunities(
      e.days,
      !!e.includeNotifications,
      !!e.includeViolations,
      this.selectedTimezone()
    ).subscribe({
      next: (resp) => {
        const disposition = resp.headers.get('Content-Disposition') || resp.headers.get('content-disposition');
        this.triggerDownload(resp.body as Blob, 'optimization-opportunities.xlsx', disposition);
        this.downloadingOpps.set(false);
      },
      error: () => {
        this.downloadingOpps.set(false);
        this.toast.error(this.i18n.instant('metrics.error_download'));
      }
    });
    this.closeOppsDialog();
  }
}


